import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { env } from '@src/config/env';
import {
    getOfflineSince,
    setOfflineSince,
    clearOfflineSince
} from '@src/config/offlineStorage';

export interface ConnectivityState {
    isOnline: boolean;
    /** When offline was first detected. Null while online. Persisted to IndexedDB. */
    offlineSince: Date | null;
}

const NetworkStatusContext = createContext<ConnectivityState | null>(null);

// TODO(API-4377 follow-up): confirm the actual lightweight 7shifts endpoint to
// HEAD-ping for reconnect confirmation. Using the API origin root as a
// placeholder until a dedicated health-check route is decided on.
const CONFIRMATION_PING_URL = `${env.apiBaseUrl}/`;

// While offline, poll for connectivity on this cadence. The browser `online`
// event only fires when the OS regains a network interface; it never fires for
// server-side/DNS/connection-refused outages where `navigator.onLine` stayed
// true. Polling `confirmConnectivity` guarantees the app eventually leaves
// offline mode — and re-triggers a deferred token refresh (API-4800) — once the
// server is reachable again, independent of the `online` event.
const RECONNECT_POLL_INTERVAL_MS = 15_000;

async function confirmConnectivity(): Promise<boolean> {
    try {
        // Any resolved response — even 4xx/5xx or an opaque `no-cors` result —
        // proves the network path to the server is up. Only a *thrown* fetch
        // (offline, DNS failure, connection refused) means we're genuinely
        // offline. `no-cors` keeps a cross-origin probe from throwing on CORS,
        // which would otherwise be indistinguishable from being offline.
        await fetch(CONFIRMATION_PING_URL, {
            method: 'HEAD',
            cache: 'no-store',
            mode: 'no-cors'
        });
        return true;
    } catch {
        return false;
    }
}

// Piggyback registry: lets non-React API modules (accountApi, TimePunchClockApi,
// etc.) report a network failure from a real API call without needing to be
// React hooks themselves. The provider registers/unregisters its offline
// transition on mount/unmount.
const piggybackListeners = new Set<() => void>();

/**
 * Call from any API layer when a real request fails due to a network error
 * (as opposed to a server/auth error). Immediately transitions the app to
 * offline — no additional request needed.
 */
export function reportNetworkError(): void {
    piggybackListeners.forEach(listener => listener());
}

/**
 * Resolves an optional backdated offline timestamp from a debug payload.
 * Accepts either `daysOffline` (a number of days in the past) or an explicit
 * `offlineSince` ISO string. Returns null when neither is a usable value.
 */
function resolveDebugOfflineSince(payload: {
    daysOffline?: unknown;
    offlineSince?: unknown;
}): Date | null {
    if (typeof payload.daysOffline === 'number' && payload.daysOffline > 0) {
        return new Date(Date.now() - payload.daysOffline * 86_400_000);
    }
    if (typeof payload.offlineSince === 'string') {
        const parsed = new Date(payload.offlineSince);
        if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return null;
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
    const [isOnline, setIsOnline] = useState<boolean>(() =>
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );
    const [offlineSince, setOfflineSinceState] = useState<Date | null>(null);
    const confirmingRef = useRef(false);

    const goOffline = useCallback(() => {
        setIsOnline(false);
        setOfflineSinceState(prev => {
            if (prev) return prev;
            const now = new Date();
            void setOfflineSince(now);
            return now;
        });
    }, []);

    // Dev-only: force offline with a backdated `offlineSince` so QA can exercise
    // the time-limit banner countdown and the lockout screen without waiting
    // real days. Unlike `goOffline`, this overwrites any existing timestamp.
    const goOfflineSince = useCallback((since: Date) => {
        setIsOnline(false);
        setOfflineSinceState(since);
        void setOfflineSince(since);
    }, []);

    const goOnline = useCallback(() => {
        setIsOnline(true);
        setOfflineSinceState(null);
        void clearOfflineSince();
    }, []);

    // Single confirmation ping — fired only when the `online` event transitions
    // false -> true. Confirms real connectivity (handles captive portals / LAN
    // without internet) before leaving offline mode.
    const confirmReconnection = useCallback(async () => {
        if (confirmingRef.current) return;
        confirmingRef.current = true;
        try {
            const confirmed = await confirmConnectivity();
            if (confirmed) {
                goOnline();
            } else {
                // OS says online, but the ping failed — stay offline. A later
                // `online` event or a successful piggybacked API call will
                // re-trigger this check.
                goOffline();
            }
        } finally {
            confirmingRef.current = false;
        }
    }, [goOffline, goOnline]);

    // Mount-time resolution: reconcile persisted offlineSince with the current
    // OS-reported connectivity. Combined into a single effect to avoid a race
    // between the async IndexedDB read and a synchronous initial online check.
    useEffect(() => {
        let cancelled = false;

        async function init() {
            const stored = await getOfflineSince();
            if (cancelled) return;

            const osOnline =
                typeof navigator !== 'undefined' ? navigator.onLine : true;

            if (!osOnline) {
                const since = stored ?? new Date();
                setIsOnline(false);
                setOfflineSinceState(since);
                if (!stored) void setOfflineSince(since);
            } else if (stored) {
                // Was offline before a refresh, but the OS now reports online.
                // Trust the OS as the fast signal; clear the stale timestamp.
                setIsOnline(true);
                setOfflineSinceState(null);
                void clearOfflineSince();
            }
        }

        void init();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        function handleOffline() {
            goOffline();
        }
        function handleOnline() {
            void confirmReconnection();
        }

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [goOffline, confirmReconnection]);

    useEffect(() => {
        piggybackListeners.add(goOffline);
        return () => {
            piggybackListeners.delete(goOffline);
        };
    }, [goOffline]);

    // While offline, poll for connectivity so we recover even when no browser
    // `online` event fires (server outage, DNS failure, connection refused —
    // `navigator.onLine` stays true throughout). `confirmReconnection` pings
    // the server and flips back online on success, which re-runs consumers'
    // effects (e.g. the deferred token refresh in useTokenRefresh — API-4800).
    useEffect(() => {
        if (isOnline) return;
        const intervalId = setInterval(() => {
            void confirmReconnection();
        }, RECONNECT_POLL_INTERVAL_MS);
        return () => {
            clearInterval(intervalId);
        };
    }, [isOnline, confirmReconnection]);

    // Dev-only debug bridge. Lets the test harness drive connectivity state
    // deterministically (without DevTools network throttling) via a
    // `7punches:debug` postMessage. Never attached in production builds.
    useEffect(() => {
        if (!env.isDev) return;

        function handleDebug(event: MessageEvent) {
            const data = event.data;
            if (
                !data ||
                typeof data !== 'object' ||
                data.type !== '7punches:debug'
            ) {
                return;
            }
            const payload = data.payload;
            if (payload?.action !== 'set_network') return;

            if (payload.online === false) {
                // Optional `daysOffline` (number) or `offlineSince` (ISO string)
                // backdates the offline timer to simulate an aged session.
                const backdated = resolveDebugOfflineSince(payload);
                if (backdated) {
                    goOfflineSince(backdated);
                } else {
                    goOffline();
                }
            } else if (payload.online === true) {
                goOnline();
            }
        }

        window.addEventListener('message', handleDebug);
        return () => {
            window.removeEventListener('message', handleDebug);
        };
    }, [goOffline, goOfflineSince, goOnline]);

    const value = useMemo<ConnectivityState>(
        () => ({ isOnline, offlineSince }),
        [isOnline, offlineSince]
    );

    return (
        <NetworkStatusContext.Provider value={value}>
            {children}
        </NetworkStatusContext.Provider>
    );
}

export function useNetworkStatus(): ConnectivityState {
    const ctx = useContext(NetworkStatusContext);
    if (!ctx) {
        throw new Error(
            'useNetworkStatus must be used within a NetworkStatusProvider'
        );
    }
    return ctx;
}
